import {
  Accordion,
  Box,
  Card,
  Group,
  Icon,
  Loader,
  Text,
  TextInput,
  Title,
  VisuallyHidden,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { KoOnly } from '@inflearn/i18n-tools';
import { Trans } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  type FormEvent,
  type ReactElement,
  type ReactNode,
  Suspense,
  useEffect,
  useState,
} from 'react';

import { css } from '@emotion/react';
import { MarkdownBody } from '@inflearn/ds-markdown-body';
import { faSparkles } from '@inflearn/pro-solid-svg-icons';
import { SuspenseQuery } from '@inflearn/query';
import { Writer } from '@inflearn/ui/dist/community';
import linkifyHtml from 'linkify-html';
import { isEmpty } from 'lodash-es';
import { ErrorBoundary } from 'react-error-boundary';
import {
  API_ENDPOINT,
  type FaqOneResponse,
  fetchClientApiV1Faqs,
} from '../../../resources/@types/uccSchema';
import { logger } from '../../../resources/Logger';
import FaqLayout from '../../components/layout/FaqLayout';
import { INFLEARN_URL } from '../../constants/urls';
import { getFaqAskOptions } from '../../queries/useFaqAskQuery';

export const getStaticProps = async ({ locale }: { locale: string }) => {
  try {
    const faq = await fetchClientApiV1Faqs(`${API_ENDPOINT.CLIENT_V1_FAQS}?usage=WEB`);

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        faqs: faq.data,
      },
      revalidate: 1,
    };
  } catch (e) {
    logger.error(e);

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
      },
      revalidate: 1,
    };
  }
};

const containerClassName = 'faq-container';
const itemClassName = 'faq-item';

export default function FaqPage({ faqs }: { faqs: FaqOneResponse[] }) {
  const [value, setValue] = useState<string | string[]>([]);
  const [searchValue, setSearchValue] = useState('');

  const handleAccordion = (selected: string | string[]) => {
    setValue(selected);
  };

  /**
   * hiddne until-found
   * 참고
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/hidden
   * https://hiddenest.dev/accessible-accordion
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect 의존성 배열 무시
  useEffect(() => {
    const container = getContainer();

    if (!container) {
      return;
    }

    const faqItem: HTMLDivElement[] = getItems() as HTMLDivElement[];

    const handleBeforeMatch = (e: Event) => {
      const target = e.target as HTMLElement;
      const index = Number(target.getAttribute('data-index'));
      const targetTitle = faqs[index].title;

      if (value.includes(targetTitle)) {
        return;
      }
      setValue((prev) => [...prev, targetTitle]);
    };

    faqItem.forEach((item) => {
      // @ts-ignore
      item.hidden = 'until-found';
      item.addEventListener('beforematch', handleBeforeMatch);
    });

    return () => {
      faqItem.forEach((item) => {
        item.removeEventListener('beforematch', handleBeforeMatch);
      });
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect 의존성 배열 무시
  useEffect(() => {
    const container = getContainer();

    if (!container) {
      return;
    }

    const faqElements = getItems() as HTMLDivElement[];
    faqElements.forEach((el) => {
      const index = Number(el.getAttribute('data-index'));
      setHiddenAttribute(el, index);
    });
  }, [value]);

  const setHiddenAttribute = (el: HTMLElement, targetIndex: number) => {
    const targetTitle = faqs[targetIndex].title;
    // @ts-ignore
    el.hidden = value.includes(targetTitle) ? undefined : 'until-found';
  };

  const getContainer = () => document.querySelector(`.${containerClassName}`);

  const getItems = () => Array.from(document.querySelectorAll(`.${itemClassName}`));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const searchValue = formData.get('search') as string;

    setSearchValue(searchValue);
  };

  return (
    <Box py={64} className={containerClassName}>
      <VisuallyHidden>
        <Title order={2}>FAQ</Title>
      </VisuallyHidden>
      <Box mb={40}>
        <Text size="lg" align="center" weight={600}>
          <Trans
            i18nKey="자주묻는질문.타이틀"
            components={{
              br: <br />,
            }}
          />
        </Text>
      </Box>

      <KoOnly>
        <Box
          component="form"
          onSubmit={handleSubmit}
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <TextInput
            icon={<Icon icon={faSparkles} />}
            maw={700}
            w={'100%'}
            size={'lg'}
            name="search"
            placeholder="AI에게 질문하기"
          />
          {!isEmpty(searchValue) && (
            <ErrorBoundary
              resetKeys={[searchValue]}
              fallback={
                <SangbaeAnswer>
                  <Text>
                    해당 부분은 제가 바로 도움드리기 어려워{' '}
                    <a href={'https://inflearn.channel.io/workflows/775833'} target={'_blank'}>
                      [상담원 연결하기]
                    </a>{' '}
                    링크를 클릭하여 문의해주세요.
                  </Text>
                </SangbaeAnswer>
              }
            >
              <Suspense
                fallback={
                  <SangbaeAnswer>
                    <Group spacing={8}>
                      <Loader variant="dots" size={'sm'} />
                      <Text>답변 생성중...</Text>
                    </Group>
                  </SangbaeAnswer>
                }
              >
                <SuspenseQuery
                  {...getFaqAskOptions({
                    searchValue,
                  })}
                >
                  {({ data }) => {
                    return (
                      <SangbaeAnswer>
                        <MarkdownBody
                          html={linkifyHtml(data.data.answer, {
                            rel: 'nofollow noreferrer noopener',
                            target: '_blank',
                          })}
                          styleWrapper={css({ background: 'inherit' })}
                        />
                      </SangbaeAnswer>
                    );
                  }}
                </SuspenseQuery>
              </Suspense>
            </ErrorBoundary>
          )}
        </Box>
      </KoOnly>

      <Box py={60}>
        <Accordion
          variant="contained"
          value={value}
          onChange={handleAccordion}
          chevronPosition="left"
          styles={{
            control: {
              [desktopFirstMediaQuery.mobile]: {
                fontSize: 13,
              },
            },
          }}
        >
          {faqs?.map((item, index) => (
            <Accordion.Item value={item.title} key={`${item.title}${item.type}`}>
              <Accordion.Control>{item.title}</Accordion.Control>
              <Box css={styleFaqItem}>
                <Box
                  p={16}
                  data-index={index}
                  className={itemClassName}
                  dangerouslySetInnerHTML={{
                    __html: item.body,
                  }}
                />
              </Box>
            </Accordion.Item>
          ))}
        </Accordion>
      </Box>
      <KoOnly>
        <Text size="lg" align="center" weight={600}>
          원하는 답변을 찾지 못하셨나요? <br />
          그럼 우측 하단의{' '}
          <Text component="a" href={INFLEARN_URL.CHANNELTALK()} color="infgreen">
            ‘문의하기’
          </Text>{' '}
          에서 직접 문의 해주세요. <br />
          최대한 빠르게 답변드리도록 하겠습니다! <br />
        </Text>
      </KoOnly>
    </Box>
  );
}

FaqPage.getLayout = (page: ReactElement) => (
  <FaqLayout
    meta={{
      title: '메타.자주묻는질문.제목',
      description: '메타.자주묻는질문.설명',
    }}
  >
    {page}
  </FaqLayout>
);

const SangbaeAnswer = ({ children }: { children: ReactNode }) => {
  return (
    <Card shadow={'sm'} withBorder p={24} bg={'gray.0'} w={'100%'}>
      <Box mb={16}>
        <Writer
          name={'인프런 AI 인턴'}
          thumbnailUrl={
            'https://cdn.inflearn.com/public/users/thumbnails/1185935/814690f1-6520-423f-be46-3e1a86b0a81c'
          }
        />
      </Box>
      {children}
    </Card>
  );
};

/**
 * ant-man css 마이그레이션
 */
const styleFaqItem = css({
  ['[hidden="until-found"]']: {
    padding: 0,
  },
  ['&:not(:has([hidden="until-found"]))']: {
    marginBottom: '8px',
    lineHeight: 1.69,
    color: '#495057',
    fontSize: '16px',
    p: {
      paddingTop: 8,
      marginBottom: 10,
    },
    img: {
      maxWidth: '100%',
    },
    a: {
      textDecoration: 'underline',
    },
    'ul, ol': {
      listStyle: 'auto',
      listStylePosition: 'inside',
      listStyleType: 'revert',
      paddingLeft: 10,
      marginBottom: 12,
    },
  },
});
